import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import { openWhatsappChat } from "../utils/whatsapp";

export interface WhatsappMessageDialogProps {
  open: boolean;
  onClose: () => void;
  phone: string;
  recipientName: string;
}

const buildDefaultMessage = (recipientName: string) => {
  const name = recipientName.trim();
  return name
    ? `Hi ${name}, this is regarding your crew assignment.`
    : "";
};

export const WhatsappMessageDialog: React.FC<WhatsappMessageDialogProps> = ({
  open,
  onClose,
  phone,
  recipientName,
}) => {
  const [message, setMessage] = useState("");
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  useEffect(() => {
    if (open) {
      setMessage(buildDefaultMessage(recipientName));
    }
  }, [open, recipientName]);

  const handleClose = () => {
    onClose();
  };

  const handleOpenWhatsapp = () => {
    openWhatsappChat(phone, message);
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WhatsAppIcon sx={{ color: "#25D366", fontSize: 28 }} />
        Message on WhatsApp
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {recipientName.trim()
            ? `Message ${recipientName.trim()}`
            : "Compose your message"}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={4}
          label="Message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Write your WhatsApp message…"
          autoFocus
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleOpenWhatsapp}
          startIcon={<WhatsAppIcon />}
          sx={{
            textTransform: "none",
            bgcolor: "#25D366",
            "&:hover": { bgcolor: "#1da851" },
            ...(theme.palette.mode === "dark" && {
              color: colors.primary[900],
            }),
          }}
        >
          Open WhatsApp
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsappMessageDialog;
